import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Loader2,
  RotateCcw,
  Search,
  Trophy,
  X,
} from 'lucide-react';
import type {
  ParticipantEvaluationProgressItem,
  ParticipantEvaluationProgressStatus,
} from '@lms/shared/types';
import {
  useParticipantEvaluationProgress,
  useResetAttempts,
  useResetProgramAttempts,
} from '../hooks/useEvaluations';
import { useLanguage } from '../lib/LanguageContext';
import { useNotificationStore } from '../store/notifications';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface ParticipantEvaluationProgressProps {
  userId: string;
  programId: string;
  programName: string;
}

interface ResetTarget {
  scope: 'EVALUATION' | 'PROGRAM';
  evaluationId?: string;
  title: string;
}

interface SummaryCard {
  key: 'total' | 'attempted' | 'passed' | 'average';
  label: { id: string; ar: string };
  value: string | number;
}

const statusStyles: Record<ParticipantEvaluationProgressStatus, string> = {
  NOT_STARTED: 'bg-surface-container-high text-on-surface-variant',
  IN_PROGRESS: 'bg-secondary-container text-on-secondary-container',
  PASSED: 'bg-success-container text-primary',
  FAILED: 'bg-error-container text-on-error-container',
};

export function ParticipantEvaluationProgress({
  userId,
  programId,
  programName,
}: ParticipantEvaluationProgressProps) {
  const { language, t } = useLanguage();
  const { addNotification } = useNotificationStore();
  const { data, isLoading, isError, refetch } = useParticipantEvaluationProgress(userId, programId);
  const resetEvaluation = useResetAttempts();
  const resetProgram = useResetProgramAttempts();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<ParticipantEvaluationProgressStatus | 'ALL'>('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<ResetTarget | null>(null);
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);

  const filteredEvaluations = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return (data?.evaluations ?? []).filter(evaluation => {
      const matchesQuery =
        !normalizedQuery ||
        evaluation.title.toLocaleLowerCase().includes(normalizedQuery) ||
        evaluation.scope.label.toLocaleLowerCase().includes(normalizedQuery);
      return matchesQuery && (status === 'ALL' || evaluation.status === status);
    });
  }, [data?.evaluations, query, status]);

  const isResetting = resetEvaluation.isPending || resetProgram.isPending;

  const clearResetDialog = () => {
    setResetTarget(null);
    setReason('');
    setConfirmation('');
    setResetError(null);
  };

  const closeResetDialog = () => {
    if (isResetting) return;
    clearResetDialog();
  };

  const handleReset = () => {
    if (!resetTarget || reason.trim().length < 5 || confirmation !== 'RESET') {
      setResetError(t(copy.users.evaluationProgress.resetValidation));
      return;
    }

    const options = {
      onSuccess: (result: { deletedAttempts: number; preservedLegacyAttempts: number }) => {
        addNotification({
          type: 'success' as const,
          title: t(copy.users.evaluationProgress.resetSuccessTitle),
          message: t(copy.users.evaluationProgress.resetSuccessMessage)
            .replace('{count}', String(result.deletedAttempts))
            .replace('{legacy}', String(result.preservedLegacyAttempts)),
        });
        clearResetDialog();
      },
      onError: (error: Error) => {
        setResetError(error.message || t(copy.users.evaluationProgress.resetError));
      },
    };

    if (resetTarget.scope === 'PROGRAM') {
      resetProgram.mutate({ programId, userId, reason: reason.trim() }, options);
      return;
    }

    if (resetTarget.evaluationId) {
      resetEvaluation.mutate(
        {
          evaluationId: resetTarget.evaluationId,
          userId,
          reason: reason.trim(),
        },
        options
      );
    }
  };

  const statusLabel = (value: ParticipantEvaluationProgressStatus) =>
    t(copy.users.evaluationProgress.status[value]);

  const summaryCards: SummaryCard[] = [
    {
      key: 'total',
      label: copy.users.evaluationProgress.total,
      value: data?.summary.totalEvaluations ?? 0,
    },
    {
      key: 'attempted',
      label: copy.users.evaluationProgress.attempted,
      value: data?.summary.attemptedEvaluations ?? 0,
    },
    {
      key: 'passed',
      label: copy.users.evaluationProgress.passed,
      value: data?.summary.passedEvaluations ?? 0,
    },
    {
      key: 'average',
      label: copy.users.evaluationProgress.average,
      value:
        data?.summary.averageScore === null || data?.summary.averageScore === undefined
          ? '-'
          : `${data.summary.averageScore}%`,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-3xl bg-surface-container-low">
        <Loader2 className="animate-spin text-primary" size={24} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-3 rounded-3xl bg-error-container p-5 text-on-error-container">
        <p className="m-0 text-sm font-bold">
          <Bilingual {...copy.users.evaluationProgress.loadError} />
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-xl bg-surface-container-lowest px-4 py-2 text-xs font-bold text-primary shadow-level1"
        >
          <Bilingual {...copy.users.evaluationProgress.retry} />
        </button>
      </div>
    );
  }

  return (
    <section className="space-y-4" aria-labelledby={`evaluation-progress-${programId}`}>
      <div className="flex flex-col gap-3 rounded-3xl bg-surface-container-low p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h5
            id={`evaluation-progress-${programId}`}
            className="m-0 text-sm font-headline font-bold text-on-surface"
          >
            <Bilingual {...copy.users.evaluationProgress.title} />
          </h5>
          <p className="m-0 mt-1 text-xs text-on-surface-variant">{programName}</p>
        </div>
        <button
          type="button"
          disabled={data.summary.totalAttempts === 0}
          onClick={() =>
            setResetTarget({
              scope: 'PROGRAM',
              title: programName,
            })
          }
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-error-container px-4 py-2 text-xs font-bold text-on-error-container shadow-level1 transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RotateCcw size={14} />
          <Bilingual {...copy.users.evaluationProgress.resetProgram} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {summaryCards.map(({ key, label, value }) => (
          <div key={key} className="rounded-2xl bg-surface-container-low p-3">
            <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              <Bilingual {...label} />
            </p>
            <p className="m-0 mt-1 text-xl font-headline font-bold text-on-surface">
              {String(value)}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-3 rounded-3xl bg-surface-container-low p-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <div>
          <label
            htmlFor={`evaluation-search-${programId}`}
            className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
          >
            <Bilingual {...copy.users.evaluationProgress.searchLabel} />
          </label>
          <div className="flex items-center gap-2 rounded-xl bg-surface-container-lowest px-3">
            <Search size={15} className="text-on-surface-variant" />
            <input
              id={`evaluation-search-${programId}`}
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={t(copy.users.evaluationProgress.searchPlaceholder)}
              className="h-11 w-full bg-transparent text-xs text-on-surface outline-none"
            />
          </div>
        </div>
        <div>
          <label
            htmlFor={`evaluation-status-${programId}`}
            className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
          >
            <Bilingual {...copy.users.evaluationProgress.filterLabel} />
          </label>
          <select
            id={`evaluation-status-${programId}`}
            value={status}
            onChange={event =>
              setStatus(event.target.value as ParticipantEvaluationProgressStatus | 'ALL')
            }
            className="h-11 w-full rounded-xl bg-surface-container-lowest px-3 text-xs font-semibold text-on-surface outline-none"
          >
            <option value="ALL">{t(copy.users.evaluationProgress.allStatuses)}</option>
            {(['NOT_STARTED', 'IN_PROGRESS', 'PASSED', 'FAILED'] as const).map(item => (
              <option key={item} value={item}>
                {statusLabel(item)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        {filteredEvaluations.length > 0 ? (
          filteredEvaluations.map(evaluation => (
            <EvaluationRow
              key={evaluation.id}
              evaluation={evaluation}
              expanded={expandedId === evaluation.id}
              statusLabel={statusLabel(evaluation.status)}
              language={language}
              expandLabel={t(copy.users.evaluationProgress.expandHistory)}
              collapseLabel={t(copy.users.evaluationProgress.collapseHistory)}
              resetLabel={t(copy.users.evaluationProgress.resetEvaluation)}
              attemptLabel={t(copy.users.evaluationProgress.attemptLabel)}
              bestScoreLabel={t(copy.users.evaluationProgress.bestScoreLabel)}
              onToggle={() =>
                setExpandedId(current => (current === evaluation.id ? null : evaluation.id))
              }
              onReset={() =>
                setResetTarget({
                  scope: 'EVALUATION',
                  evaluationId: evaluation.id,
                  title: evaluation.title,
                })
              }
            />
          ))
        ) : (
          <div className="rounded-3xl bg-surface-container-low p-6 text-center text-xs text-on-surface-variant">
            <Bilingual {...copy.users.evaluationProgress.empty} />
          </div>
        )}
      </div>

      {resetTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-on-surface/45 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="evaluation-reset-title"
            className="w-full max-w-lg space-y-5 rounded-[2rem] bg-surface-container-lowest p-6 shadow-level5 sm:p-8"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-error-container text-on-error-container">
                  <AlertTriangle size={20} />
                </span>
                <div>
                  <h4
                    id="evaluation-reset-title"
                    className="m-0 text-lg font-headline font-bold text-on-surface"
                  >
                    <Bilingual
                      {...(resetTarget.scope === 'PROGRAM'
                        ? copy.users.evaluationProgress.resetProgramTitle
                        : copy.users.evaluationProgress.resetEvaluationTitle)}
                    />
                  </h4>
                  <p className="m-0 mt-1 text-xs text-on-surface-variant">{resetTarget.title}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeResetDialog}
                aria-label={t(copy.common.cancel)}
                className="rounded-xl p-2 text-on-surface-variant hover:bg-surface-container-high"
              >
                <X size={18} />
              </button>
            </div>

            <p className="m-0 rounded-2xl bg-error-container p-4 text-xs leading-relaxed text-on-error-container">
              <Bilingual {...copy.users.evaluationProgress.resetWarning} />
            </p>

            <div>
              <label
                htmlFor="evaluation-reset-reason"
                className="mb-1.5 block text-xs font-bold text-on-surface"
              >
                <Bilingual {...copy.users.evaluationProgress.reasonLabel} />
              </label>
              <textarea
                id="evaluation-reset-reason"
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder={t(copy.users.evaluationProgress.reasonPlaceholder)}
                className="min-h-24 w-full resize-y rounded-2xl bg-surface-container-low p-3 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/25"
              />
            </div>

            <div>
              <label
                htmlFor="evaluation-reset-confirmation"
                className="mb-1.5 block text-xs font-bold text-on-surface"
              >
                <Bilingual {...copy.users.evaluationProgress.confirmationLabel} />
              </label>
              <input
                id="evaluation-reset-confirmation"
                value={confirmation}
                onChange={event => setConfirmation(event.target.value.toUpperCase())}
                placeholder="RESET"
                autoComplete="off"
                className="h-12 w-full rounded-2xl bg-surface-container-low px-4 font-mono text-sm font-bold uppercase text-on-surface outline-none focus:ring-2 focus:ring-primary/25"
              />
            </div>

            {resetError && (
              <p
                role="alert"
                className="m-0 rounded-xl bg-error-container px-3 py-2 text-xs text-on-error-container"
              >
                {resetError}
              </p>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeResetDialog}
                disabled={isResetting}
                className="min-h-11 rounded-xl bg-surface-container-high px-5 text-xs font-bold text-on-surface"
              >
                <Bilingual {...copy.common.cancel} />
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isResetting || reason.trim().length < 5 || confirmation !== 'RESET'}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-error px-5 text-xs font-bold text-on-error shadow-level1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isResetting ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <RotateCcw size={15} />
                )}
                <Bilingual {...copy.users.evaluationProgress.confirmReset} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EvaluationRow({
  evaluation,
  expanded,
  statusLabel,
  language,
  expandLabel,
  collapseLabel,
  resetLabel,
  attemptLabel,
  bestScoreLabel,
  onToggle,
  onReset,
}: {
  evaluation: ParticipantEvaluationProgressItem;
  expanded: boolean;
  statusLabel: string;
  language: 'id' | 'ar';
  expandLabel: string;
  collapseLabel: string;
  resetLabel: string;
  attemptLabel: string;
  bestScoreLabel: string;
  onToggle: () => void;
  onReset: () => void;
}) {
  return (
    <article className="rounded-3xl bg-surface-container-low p-3.5 sm:p-4">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? collapseLabel : expandLabel}: ${evaluation.title}`}
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-container-lowest text-primary shadow-level1"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-lg bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
              {evaluation.type}
            </span>
            <span
              className={`rounded-lg px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statusStyles[evaluation.status]}`}
            >
              {statusLabel}
            </span>
          </div>
          <h6 className="m-0 mt-2 text-sm font-bold text-on-surface">{evaluation.title}</h6>
          <p className="m-0 mt-1 text-[11px] text-on-surface-variant">
            {evaluation.scope.label} · {evaluation.attemptCount} {attemptLabel} · {bestScoreLabel}{' '}
            {evaluation.bestScore ?? '-'}
          </p>
        </div>
        <button
          type="button"
          onClick={onReset}
          disabled={evaluation.attemptCount === 0}
          aria-label={`${resetLabel}: ${evaluation.title}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-error-container text-on-error-container transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-35"
        >
          <RotateCcw size={15} />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2 rounded-2xl bg-surface-container-lowest p-3">
          {evaluation.attempts.length > 0 ? (
            evaluation.attempts.map((attempt, index) => (
              <div
                key={attempt.id}
                className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-xl bg-surface-container-low p-2.5 text-[11px]"
              >
                {attempt.isPassed ? (
                  <CheckCircle2 size={15} className="text-primary" />
                ) : attempt.submittedAt ? (
                  <Trophy size={15} className="text-error" />
                ) : (
                  <Clock3 size={15} className="text-secondary" />
                )}
                <div>
                  <p className="m-0 font-bold text-on-surface">
                    {attemptLabel} #{evaluation.attempts.length - index}
                  </p>
                  <p className="m-0 text-on-surface-variant">
                    {new Date(attempt.startedAt).toLocaleString(
                      language === 'ar' ? 'ar-SA' : 'id-ID'
                    )}
                  </p>
                </div>
                <span className="font-headline text-sm font-bold text-on-surface">
                  {attempt.score ?? '-'}
                </span>
              </div>
            ))
          ) : (
            <p className="m-0 py-2 text-center text-xs text-on-surface-variant">
              <Bilingual {...copy.users.evaluationProgress.noAttempts} />
            </p>
          )}
        </div>
      )}
    </article>
  );
}
